<template>
  <div class="template-manager-panel">
    <section class="panel-header">
      <div>
        <h3>接口模板管理</h3>
        <p>查看内置请求类型，或用普通字段创建自定义测试模板</p>
      </div>
      <el-tag effect="plain" type="success">{{ templates.length }} 个模板</el-tag>
    </section>

    <div class="manager-layout">
      <section class="config-section">
        <div class="section-heading">
          <div>
            <h4>模板列表</h4>
            <p>内置模板与正式业务字段保持一致</p>
          </div>
          <div class="heading-actions">
            <el-button :icon="Refresh" plain @click="refreshTemplates">刷新</el-button>
            <el-button :icon="Plus" type="primary" @click="resetEditor">新增</el-button>
          </div>
        </div>

        <el-table :data="templates" border size="small" class="template-table">
          <el-table-column prop="funcType" label="功能号" width="76" align="center" />
          <el-table-column label="模板" min-width="220">
            <template #default="{ row }">
              <div class="template-cell"><strong>{{ row.title }}</strong><small>{{ row.funcPath }}</small></div>
            </template>
          </el-table-column>
          <el-table-column label="来源" width="90" align="center">
            <template #default="{ row }"><el-tag :type="row.builtin ? 'info' : 'warning'" effect="plain">{{ row.builtin ? '内置' : '自定义' }}</el-tag></template>
          </el-table-column>
          <el-table-column label="操作" width="128" align="center">
            <template #default="{ row }">
              <el-button v-if="!row.builtin" :icon="Edit" plain circle size="small" @click="loadEditor(row)" />
              <span v-else class="builtin-note">请求页可修改</span>
              <el-popconfirm v-if="!row.builtin" title="删除这个自定义模板？" confirm-button-text="删除" cancel-button-text="取消" @confirm="handleDelete(row.key)">
                <template #reference><el-button :icon="Delete" type="danger" plain circle size="small" /></template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section class="config-section">
        <div class="section-heading">
          <div>
            <h4>{{ editingKey ? '编辑自定义模板' : '新增自定义模板' }}</h4>
            <p>保存后可在模拟请求页直接选择</p>
          </div>
        </div>

        <el-form label-position="top" class="editor-form">
          <div class="field-grid">
            <el-form-item label="模板标识"><el-input v-model.trim="editor.key" placeholder="new-func-33" :disabled="Boolean(editingKey)" clearable /></el-form-item>
            <el-form-item label="模板名称"><el-input v-model.trim="editor.title" placeholder="33 新功能" clearable /></el-form-item>
            <el-form-item label="功能号"><el-input v-model.trim="editor.funcType" placeholder="33" clearable /></el-form-item>
            <el-form-item label="接口路径"><el-input v-model.trim="editor.funcPath" placeholder="NewFunc33" clearable /></el-form-item>
          </div>

          <div class="parameter-heading">
            <div><strong>默认功能参数</strong><small>逐项填写，模拟请求页仍可修改</small></div>
          </div>

          <div v-if="parameterFields.length" class="parameter-list">
            <div v-for="field in parameterFields" :key="field.path" class="parameter-row">
              <el-input :model-value="field.path" disabled aria-label="参数名" />
              <el-input-number v-if="field.input === 'number'" :model-value="getParameterValue(parm, field.path)" controls-position="right" @update:model-value="setParameterValue(parm, field.path, $event)" />
              <el-input v-else :model-value="getParameterValue(parm, field.path)" aria-label="参数值" @update:model-value="setParameterValue(parm, field.path, $event)" />
              <el-button :icon="Delete" type="danger" plain circle @click="removeField(field.path)" />
            </div>
          </div>
          <el-empty v-else description="暂无额外参数" :image-size="58" />

          <div class="add-parameter">
            <el-input v-model.trim="newField.name" placeholder="参数名，例如 ModAddr" />
            <el-select v-model="newField.type" aria-label="参数类型">
              <el-option label="文本" value="text" />
              <el-option label="数字" value="number" />
            </el-select>
            <el-input v-model="newField.value" placeholder="默认值" />
            <el-button :icon="Plus" plain @click="addField">添加参数</el-button>
          </div>

          <div class="form-actions">
            <el-button plain @click="resetEditor">清空</el-button>
            <el-button :icon="Select" type="primary" @click="saveEditor">保存模板</el-button>
          </div>
        </el-form>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { Delete, Edit, Plus, Refresh, Select } from '@element-plus/icons-vue';
import {
  flattenParameterFields,
  getAllSimulatorTemplates,
  getParameterValue,
  removeCustomTemplate,
  removeParameterValue,
  setParameterValue,
  upsertCustomTemplate,
} from '@/utils/fordDiagnosticSimulatorUi.mjs';

const templates = ref([]);
const editingKey = ref('');
const parm = reactive({});
const editor = reactive({ key: '', title: '', funcType: '33', funcPath: '' });
const newField = reactive({ name: '', type: 'text', value: '' });
const parameterFields = computed(() => flattenParameterFields(parm));

function notifyChanged() {
  window.dispatchEvent(new CustomEvent('ford-simulator-templates-changed'));
}

function refreshTemplates() {
  templates.value = getAllSimulatorTemplates();
}

function replaceParm(value) {
  Object.keys(parm).forEach((key) => delete parm[key]);
  Object.assign(parm, JSON.parse(JSON.stringify(value || {})));
}

function resetEditor() {
  editingKey.value = '';
  Object.assign(editor, { key: '', title: '', funcType: '33', funcPath: '' });
  replaceParm({});
  Object.assign(newField, { name: '', type: 'text', value: '' });
}

function loadEditor(template) {
  editingKey.value = template.key;
  Object.assign(editor, { key: template.key, title: template.title, funcType: String(template.funcType), funcPath: template.funcPath });
  replaceParm(template.parm);
}

function addField() {
  if (!newField.name) {
    ElMessage.warning('请先填写参数名');
    return;
  }
  if (parameterFields.value.some((field) => field.path === newField.name)) {
    ElMessage.warning('参数名已存在');
    return;
  }
  const value = newField.type === 'number' ? Number(newField.value || 0) : newField.value;
  setParameterValue(parm, newField.name, value);
  Object.assign(newField, { name: '', type: 'text', value: '' });
}

function removeField(path) {
  removeParameterValue(parm, path);
}

function saveEditor() {
  try {
    const saved = upsertCustomTemplate({ ...editor, parm });
    refreshTemplates();
    notifyChanged();
    loadEditor(saved.find((item) => item.key === editor.key));
    ElMessage.success('模板已保存');
  } catch (error) {
    ElMessage.warning(error.message || '保存失败');
  }
}

function handleDelete(key) {
  removeCustomTemplate(key);
  refreshTemplates();
  notifyChanged();
  if (editingKey.value === key) resetEditor();
  ElMessage.success('自定义模板已删除');
}

refreshTemplates();
</script>

<style scoped>
.template-manager-panel { display: grid; gap: 14px; min-width: 0; }
.panel-header, .config-section { border: 1px solid #dfe8f3; border-radius: 8px; background: #fff; box-shadow: 0 10px 24px rgba(31, 56, 88, .05); }
.panel-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 16px 18px; }
.panel-header h3, .section-heading h4 { margin: 0; color: #132033; font-weight: 800; }
.panel-header h3 { font-size: 18px; }
.panel-header p, .section-heading p { margin: 5px 0 0; color: #6b7b8f; font-size: 13px; line-height: 1.45; }
.manager-layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(390px, .82fr); gap: 14px; align-items: start; }
.config-section { min-width: 0; padding: 16px; }
.section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.heading-actions, .form-actions { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
.template-table { width: 100%; }
.template-table :deep(.el-table__header th) { background: #f6f9fc !important; color: #4b5f75; font-weight: 800; }
.template-cell { display: grid; gap: 3px; min-width: 0; }
.template-cell strong, .template-cell small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.template-cell small, .builtin-note { color: #7a8a9d; font-size: 12px; }
.field-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 12px; }
.editor-form :deep(.el-form-item) { margin-bottom: 14px; }
.parameter-heading { display: flex; justify-content: space-between; margin: 4px 0 10px; }
.parameter-heading div { display: grid; gap: 3px; }
.parameter-heading small { color: #7a8a9d; }
.parameter-list { display: grid; gap: 8px; margin-bottom: 12px; }
.parameter-row { display: grid; grid-template-columns: minmax(120px, .8fr) minmax(160px, 1fr) 36px; gap: 8px; align-items: center; }
.parameter-row :deep(.el-input-number) { width: 100%; }
.add-parameter { display: grid; grid-template-columns: minmax(0, 1fr) 104px; gap: 8px; padding: 12px; margin-bottom: 14px; border: 1px dashed #cddceb; border-radius: 8px; background: #f8fbfe; }
@media (max-width: 1180px) { .manager-layout { grid-template-columns: 1fr; } }
@media (max-width: 700px) { .panel-header, .section-heading { flex-direction: column; } .field-grid { grid-template-columns: 1fr; } .parameter-row, .add-parameter { grid-template-columns: 1fr; } .heading-actions, .form-actions { justify-content: flex-start; } }
</style>
